import type { ButtonHTMLAttributes, ReactNode } from "react";
import { classNames } from "../../classNames";
import { Icon, type IconName } from "../../icons";

export type ButtonVariant = "primary" | "accent" | "secondary" | "ghost" | "danger" | "danger-soft";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** Visible text (or the accessible name when `iconOnly`). */
  label?: string;
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  iconRight?: IconName;
  iconOnly?: boolean;
  /** Shows a spinner, keeps the width and blocks clicks. */
  loading?: boolean;
  block?: boolean;
  type?: "button" | "submit";
}

const SIZE_CLASS: Record<NonNullable<ButtonProps["size"]>, string | null> = { sm: "nb-btn-sm", md: null, lg: "nb-btn-lg" };

export function Button({
  label,
  children,
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  iconOnly,
  loading = false,
  block,
  type = "button",
  className,
  disabled,
  onClick,
  ...rest
}: ButtonProps): React.JSX.Element {
  const classes = classNames("nb-btn", `nb-btn-${variant}`, SIZE_CLASS[size], iconOnly ? "nb-btn-icon" : null, block ? "nb-btn-block" : null, className);

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-label={iconOnly ? label : rest["aria-label"]}
      title={iconOnly ? label : undefined}
      onClick={(e) => {
        // While loading the click is swallowed: the action is already running.
        if (loading) e.preventDefault();
        else onClick?.(e);
      }}
      {...rest}
    >
      {loading ? <span className="nb-spinner" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
      {iconOnly ? null : <span>{children ?? label}</span>}
      {iconRight && !iconOnly ? <Icon name={iconRight} className="nb-btn-trail" /> : null}
    </button>
  );
}
